import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2, Clock, CheckCircle2, XCircle, Plus } from 'lucide-react';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { formatMoney } from '../lib/money';
import api from '../services/api';
import { toast } from 'sonner';

const STATUS_UI = {
  pending: { label: 'Pending approval', icon: Clock, cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  approved: { label: 'Live', icon: CheckCircle2, cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  rejected: { label: 'Rejected', icon: XCircle, cls: 'bg-rose-50 text-rose-700 border-rose-200' },
};

export default function MarketplaceMine() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    api.get('/marketplace/listings/mine')
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (id) => {
    if (!window.confirm('Delete this listing?')) return;
    try {
      await api.delete(`/marketplace/listings/${id}`);
      toast.success('Listing deleted');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header showSearch={false} />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => nav('/marketplace')} className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 mb-4">
          <ArrowLeft className="h-4 w-4 mr-1" /> Marketplace
        </button>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">My listings</h1>
          <Button onClick={() => nav('/marketplace/new')} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="mine-sell-btn">
            <Plus className="h-4 w-4 mr-1" /> Sell an item
          </Button>
        </div>

        {loading ? (
          <div className="mt-12 text-center text-slate-400">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="mt-10 bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-500">
            You haven't listed anything yet.
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            {rows.map((l) => {
              const s = STATUS_UI[l.status] || STATUS_UI.pending;
              const Icon = s.icon;
              return (
                <div key={l.listing_id} className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-4" data-testid={`mine-listing-${l.listing_id}`}>
                  {l.photos?.[0] && <img src={l.photos[0]} alt="" className="h-16 w-16 rounded-lg object-cover" />}
                  <div className="flex-1 min-w-0">
                    <Link to={`/marketplace/item/${l.listing_id}`} className="font-medium text-slate-900 truncate hover:underline">{l.title}</Link>
                    <p className="text-sm text-emerald-700 font-semibold">{formatMoney(l.price, l.currency)}</p>
                    <span className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${s.cls}`} data-testid={`mine-status-${l.listing_id}`}>
                      <Icon className="h-3 w-3" /> {s.label}
                    </span>
                    {l.status === 'rejected' && l.rejection_reason && (
                      <p className="text-xs text-rose-600 mt-1">Reason: {l.rejection_reason}</p>
                    )}
                  </div>
                  <button onClick={() => remove(l.listing_id)} className="text-slate-400 hover:text-rose-500" data-testid={`mine-delete-${l.listing_id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
